import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import ProgressIndicator from '../../components/ui/ProgressIndicator';
import AssessmentNavigation from '../../components/ui/AssessmentNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CustomerTable from './components/CustomerTable';
import MarketRiskVisualization from './components/MarketRiskVisualization';
import CustomerFormModal from './components/CustomerFormModal';
import MarketExpansionAnalysis from './components/MarketExpansionAnalysis';
import SeasonalDemandChart from './components/SeasonalDemandChart';

const MarketAssessment = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('customers');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [customers, setCustomers] = useState([
    {
      id: 1,
      name: 'Dinas Pendidikan Provinsi Jawa Tengah',
      type: 'Institusi Pemerintah',
      volume: 120000,
      paymentTerms: 'Net 30',
      contractDuration: '2 tahun',
      dependencyPercentage: 35.2
    },
    {
      id: 2,
      name: 'Indomaret Group',
      type: 'Retail Modern',
      volume: 85000,
      paymentTerms: 'Net 14',
      contractDuration: '1 tahun',
      dependencyPercentage: 24.9
    },
    {
      id: 3,
      name: 'PT Distribusi Susu Nusantara',
      type: 'Distributor',
      volume: 65000,
      paymentTerms: 'Net 7',
      contractDuration: '1 tahun',
      dependencyPercentage: 19.1
    },
    {
      id: 4,
      name: 'Alfamart Regional Jateng',
      type: 'Retail Modern',
      volume: 42000,
      paymentTerms: 'Net 14',
      contractDuration: '6 bulan',
      dependencyPercentage: 12.3
    },
    {
      id: 5,
      name: 'Hotel Santika Chain',
      type: 'Korporat Swasta',
      volume: 18000,
      paymentTerms: 'Net 30',
      contractDuration: '1 tahun',
      dependencyPercentage: 5.3
    },
    {
      id: 6,
      name: 'Warung Susu Berkah',
      type: 'UMKM',
      volume: 8500,
      paymentTerms: 'Cash',
      contractDuration: 'Tidak ada kontrak',
      dependencyPercentage: 2.5
    }
  ]);

  const handleAddCustomer = () => {
    setEditingCustomer(null);
    setIsModalOpen(true);
  };

  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setIsModalOpen(true);
  };

  const handleDeleteCustomer = (id) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus pelanggan ini?')) {
      setCustomers(customers?.filter(c => c?.id !== id));
    }
  };

  const handleSubmitCustomer = (customerData) => {
    if (editingCustomer) {
      setCustomers(customers?.map(c => 
        c?.id === editingCustomer?.id ? { ...customerData, id: c?.id } : c
      ));
    } else {
      const newCustomer = {
        ...customerData,
        id: Math.max(...customers?.map(c => c?.id), 0) + 1
      };
      setCustomers([...customers, newCustomer]);
    }
    setIsModalOpen(false);
    setEditingCustomer(null);
  };

  const handleSaveAndContinue = () => {
    navigate('/governance-evaluation');
  };

  const handleBack = () => {
    navigate('/financial-analysis-form');
  };

  const tabs = [
    { id: 'customers', label: 'Portofolio Pelanggan', icon: 'Users' },
    { id: 'risk', label: 'Analisis Risiko', icon: 'AlertTriangle' },
    { id: 'expansion', label: 'Ekspansi Pasar', icon: 'Target' },
    { id: 'seasonal', label: 'Pola Musiman', icon: 'Calendar' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ProgressIndicator currentStep={6} completedSteps={[1, 2, 3, 4, 5]} />
      <AssessmentNavigation completedModules={['supplier', 'operational', 'equipment', 'financial']} />
      <main className="pt-44 pb-12">
        <div className="container-executive">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Icon name="TrendingUp" size={24} color="var(--color-primary)" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-foreground">Penilaian Pasar</h1>
                <p className="text-base md:text-lg text-muted-foreground">
                  Analisis ketergantungan pelanggan dan evaluasi risiko pasar
                </p>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="bg-card rounded-xl border border-border mb-6 overflow-x-auto">
            <div className="flex gap-2 p-2 min-w-max">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-medium transition-all ${
                    activeTab === tab?.id
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {activeTab === 'customers' && (
              <>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-1">Daftar Pelanggan</h2>
                    <p className="text-sm text-muted-foreground">
                      Kelola portofolio pelanggan dan analisis ketergantungan
                    </p>
                  </div>
                  <Button
                    variant="default"
                    onClick={handleAddCustomer}
                    iconName="Plus"
                    iconPosition="left"
                  >
                    Tambah Pelanggan
                  </Button>
                </div>

                <div className="bg-card rounded-xl border border-border p-6">
                  <CustomerTable
                    customers={customers}
                    onEdit={handleEditCustomer}
                    onDelete={handleDeleteCustomer}
                  />
                </div>
              </>
            )}

            {activeTab === 'risk' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-card rounded-xl border border-border p-6">
                  <h2 className="text-xl font-semibold text-foreground mb-6 flex items-center gap-2">
                    <Icon name="AlertTriangle" size={20} color="var(--color-primary)" />
                    Visualisasi Risiko Pasar
                  </h2>
                  <MarketRiskVisualization customers={customers} />
                </div>

                <div className="space-y-6">
                  <div className="bg-card rounded-xl border border-border p-6">
                    <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                      <Icon name="Shield" size={20} color="var(--color-primary)" />
                      Rekomendasi Mitigasi
                    </h3>
                    <div className="space-y-4">
                      <div className="flex gap-3 p-4 rounded-lg bg-warning/10 border border-warning/20">
                        <Icon name="AlertCircle" size={20} className="text-warning flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-foreground mb-1">Diversifikasi Pelanggan</p>
                          <p className="text-sm text-muted-foreground">
                            Kurangi ketergantungan pada 3 pelanggan teratas dengan menambah 5-7 pelanggan baru dari segmen berbeda
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-3 p-4 rounded-lg bg-primary/10 border border-primary/20">
                        <Icon name="Target" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-foreground mb-1">Kontrak Jangka Panjang</p>
                          <p className="text-sm text-muted-foreground">
                            Negosiasi perpanjangan kontrak minimal 2 tahun dengan pelanggan utama untuk stabilitas
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-3 p-4 rounded-lg bg-success/10 border border-success/20">
                        <Icon name="TrendingUp" size={20} className="text-success flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-foreground mb-1">Ekspansi Segmen Retail</p>
                          <p className="text-sm text-muted-foreground">
                            Fokus pada retail modern dan e-commerce untuk mengurangi risiko konsentrasi institusi
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-error/10 to-warning/10 rounded-xl border border-border p-6">
                    <div className="flex items-start gap-3 mb-4">
                      <Icon name="AlertTriangle" size={24} className="text-error flex-shrink-0" />
                      <div>
                        <h3 className="text-lg font-semibold text-foreground mb-1">Peringatan Risiko</h3>
                        <p className="text-sm text-muted-foreground">
                          Ketergantungan tinggi terdeteksi pada pelanggan institusi pemerintah
                        </p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Tingkat Risiko</span>
                        <span className="font-semibold text-error">Tinggi</span>
                      </div>
                      <div className="h-2 bg-background rounded-full overflow-hidden">
                        <div className="h-full bg-error w-4/5 transition-all duration-500" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'expansion' && (
              <div className="bg-card rounded-xl border border-border p-6">
                <h2 className="text-xl font-semibold text-foreground mb-6 flex items-center gap-2">
                  <Icon name="Target" size={20} color="var(--color-primary)" />
                  Analisis Ekspansi Pasar
                </h2>
                <MarketExpansionAnalysis customers={customers} />
              </div>
            )}

            {activeTab === 'seasonal' && (
              <div className="bg-card rounded-xl border border-border p-6">
                <SeasonalDemandChart />
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mt-8">
            <Button
              variant="outline"
              size="lg"
              onClick={handleBack}
              iconName="ArrowLeft"
              iconPosition="left"
              className="sm:w-auto w-full"
            >
              Kembali
            </Button>
            <Button
              variant="default"
              size="lg"
              onClick={handleSaveAndContinue}
              iconName="ArrowRight"
              iconPosition="right"
              className="sm:flex-1 w-full"
            >
              Simpan & Lanjutkan ke Evaluasi Tata Kelola
            </Button>
          </div>
        </div>
      </main>
      <CustomerFormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingCustomer(null);
        }}
        onSubmit={handleSubmitCustomer}
        editingCustomer={editingCustomer}
      />
    </div>
  );
};

export default MarketAssessment;